import { Subscription } from '@wix/ambassador-billing-v1-subscription/types';
import React from 'react';
import { TextButton } from 'wix-ui-tpa';
import classNames from 'classnames';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { connect } from '../../../../common/components/runtime-context';
import { IActionCreators } from '../../../../types/internal-types';
import { RootState } from '../../state';
import { getMinEarlyEndNoticeDays, getMinRevocationNoticeDays, getRegionalSettings } from '../../selectors';
import {
  getSkipCyclesCount,
  getSkipResumeDate,
  isCurrentlySkipping,
  isInLastSkippedCycle,
  isResumeTooClose,
  isUnskipTooClose,
} from '../../domainUtils';
import { formatDate } from '../../../../utils/displayUtils';
import { DisabledCtaTooltip } from './DisabledCtaTooltip';
import { classes } from './SubscriptionsListItemBass.st.css';

export const CANCEL_SCHEDULED_SKIP_CTA_DATA_HOOKS = {
  button: 'subscription-cancel-scheduled-skip-button',
  tooltip: 'subscription-cancel-scheduled-skip-tooltip',
};

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

interface Props {
  subscription: Subscription;
}

const CancelScheduledSkipCta = ({
  subscription,
  currentlySkipping,
  lastSkippedCycle,
  disabled,
  noticeDays,
  regionalSettings,
  actionCreators,
}: Props & RuntimeProps) => {
  const { t } = useTranslation();
  const unskipLabelKey =
    getSkipCyclesCount(subscription) > 1
      ? 'app.subscription.overviewModal.cancelScheduledSkipsCta'
      : 'app.subscription.overviewModal.cancelScheduledSkipsCtaSingular';
  const labelKey = currentlySkipping ? 'app.subscription.overviewModal.resumeSkipCta' : unskipLabelKey;
  const tooltipKey = currentlySkipping
    ? 'app.subscription-status.popover.tooltip.resumeTooCloseMessage'
    : 'app.subscription-status.popover.tooltip.unskipTooCloseMessage';

  const getTooltipContent = () => {
    if (lastSkippedCycle) {
      const resumeDate = getSkipResumeDate(subscription);
      return t('app.subscription-status.popover.tooltip.resumeNoEarlierMessage', {
        resumeDate: formatDate(resumeDate, regionalSettings),
        interpolation: { escapeValue: false },
      });
    }
    return t(tooltipKey, { count: noticeDays });
  };

  const button = (
    <TextButton
      data-hook={CANCEL_SCHEDULED_SKIP_CTA_DATA_HOOKS.button}
      className={classNames(classes.cta, classes.ctaNoWrap)}
      disabled={disabled}
      onClick={() => {
        actionCreators.openCancelScheduledSkipConfirmModal(subscription.id!);
      }}
    >
      {t(labelKey)}
    </TextButton>
  );

  if (!disabled) {
    return button;
  }

  return (
    <DisabledCtaTooltip dataHook={CANCEL_SCHEDULED_SKIP_CTA_DATA_HOOKS.tooltip} content={getTooltipContent()}>
      {button}
    </DisabledCtaTooltip>
  );
};

const mapRuntimeToProps = (state: RootState, { subscription }: Props, actionCreators: IActionCreators) => {
  const currentlySkipping = isCurrentlySkipping(subscription);
  const now = new Date();
  const noticeDays = currentlySkipping
    ? getMinEarlyEndNoticeDays(state, subscription)
    : getMinRevocationNoticeDays(state, subscription);
  const lastSkippedCycle = currentlySkipping && isInLastSkippedCycle(subscription, now);
  const tooClose = currentlySkipping
    ? isResumeTooClose(subscription, noticeDays, now)
    : isUnskipTooClose(subscription, noticeDays, now);
  return {
    actionCreators,
    currentlySkipping,
    lastSkippedCycle,
    noticeDays,
    regionalSettings: getRegionalSettings(state),
    disabled: lastSkippedCycle || tooClose,
  };
};

export default connect(mapRuntimeToProps)(CancelScheduledSkipCta);
